import type { Content, ListItem } from 'mdast';
import { fg } from '@atlaskit/platform-feature-flags';
import type { State } from '../state';
import { isMalformedTaskItem } from '../utils/malformed-task-utils';
import { stripMalformedCheckboxPrefix } from '../utils/malformed-checkbox-strip';

/**
 * Converts an mdast ListItem node to a ProseMirror taskItem or list_item node.
 */
export function listItem(state: State, node: ListItem): void {
	// Detects a remark-gfm "malformed" checkbox: checked: null but has a [ ]/[x]/[X] text prefix.
	const isMalformedTask = node.checked === null && isMalformedTaskItem(node);

	// Gate: malformed items are rescued as unchecked task items when ON.
	const isTask = fg('platform_editor_markdown_patch_m3')
		? isMalformedTask || (node.checked !== null && node.checked !== undefined)
		: node.checked !== null && node.checked !== undefined;

	// If the schema has a dedicated taskItem node (ADF style), use it
	const taskItemType = state.schema.nodes.taskItem ?? state.schema.nodes.task_item;
	if (isTask && taskItemType) {
		const taskAttrs: Record<string, unknown> = {};
		const attrSpec = taskItemType.spec.attrs ?? {};
		if ('state' in attrSpec) {
			// ADF convention: state is 'DONE' or 'TODO'.
			if (fg('platform_editor_markdown_patch_m3')) {
				taskAttrs.state = isMalformedTask ? 'TODO' : node.checked ? 'DONE' : 'TODO';
			} else {
				taskAttrs.state = node.checked ? 'DONE' : 'TODO';
			}
		}
		if ('checked' in attrSpec) {
			if (fg('platform_editor_markdown_patch_m3')) {
				taskAttrs.checked = isMalformedTask ? false : node.checked;
			} else {
				taskAttrs.checked = node.checked;
			}
		}

		state.openNode(taskItemType, taskAttrs);
		// taskItem is `inline*`; defer block children so they emit as siblings of
		// the taskItem inside the parent taskList (ADF's nested-task-list shape).
		const deferredBlockChildren: Content[] = [];

		for (const child of node.children) {
			if (child.type === 'paragraph') {
				if (fg('platform_editor_markdown_patch_m3')) {
					// Strip the `[ ]` prefix from the first paragraph of a malformed item
					// so it doesn't appear as literal content in the editor.
					if (isMalformedTask && child === node.children[0]) {
						state.parseTokens(stripMalformedCheckboxPrefix(child.children));
					} else {
						state.parseTokens(child.children);
					}
				} else {
					state.parseTokens(child.children);
				}
			} else if (fg('platform_editor_fix_nested_markdown_tasks')) {
				deferredBlockChildren.push(child);
			} else {
				state.parseTokens([child]);
			}
		}
		state.closeNode();
		if (deferredBlockChildren.length > 0) {
			state.parseTokens(deferredBlockChildren);
		}
		return;
	}

	// Regular list item (or task item falling back to list_item when no taskItem node)
	const itemType = state.schema.nodes.list_item ?? state.schema.nodes.listItem;
	if (!itemType) {
		// Gracefully degrade: schema has no list item node — render children directly
		state.parseTokens(node.children);
		return;
	}
	const attrs: Record<string, unknown> = {};
	if ('checked' in (itemType.spec.attrs ?? {})) {
		// Always write the checked attr when the schema supports it (null for non-task items)
		attrs.checked = isTask ? node.checked : null;
	}

	state.openNode(itemType, attrs);
	if (node.children.length === 0) {
		// Empty list item (e.g. `* ` with no content) — insert an empty paragraph
		// so the ADF listItem node satisfies the schema's content requirement
		// (listItem requires at least one paragraph child). Without this, the
		// empty listItem is dropped during PM node normalization.
		const paragraphType = state.schema.nodes.paragraph;
		if (paragraphType) {
			state.push(paragraphType.create());
		}
	} else {
		state.parseTokens(node.children);
	}
	state.closeNode();
}
